import React from 'react';
import { cn } from '@/lib/utils';

interface LiveRegionProps {
  children: React.ReactNode;
  politeness?: 'polite' | 'assertive' | 'off';
  atomic?: boolean;
  relevant?: 'additions' | 'removals' | 'text' | 'all';
  className?: string;
  id?: string;
}

export function LiveRegion({ 
  children, 
  politeness = 'polite', 
  atomic = false, 
  relevant = 'additions',
  className,
  id
}: LiveRegionProps) {
  return (
    <div
      id={id}
      aria-live={politeness}
      aria-atomic={atomic}
      aria-relevant={relevant}
      className={cn('sr-only', className)}
    >
      {children}
    </div>
  );
}

interface AnnouncementProps {
  message: string;
  priority?: 'polite' | 'assertive';
  id?: string;
}

export function Announcement({ message, priority = 'polite', id }: AnnouncementProps) {
  return (
    <LiveRegion 
      politeness={priority}
      atomic={true}
      id={id}
    >
      {message}
    </LiveRegion>
  );
}

interface LiveRegionContextValue {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  announceStatus: (message: string) => void;
  announceAlert: (message: string) => void;
}

const LiveRegionContext = React.createContext<LiveRegionContextValue | null>(null);

export function LiveRegionProvider({ children }: { children: React.ReactNode }) {
  const politeRef = React.useRef<HTMLDivElement>(null);
  const assertiveRef = React.useRef<HTMLDivElement>(null);

  const announce = React.useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const element = priority === 'polite' ? politeRef.current : assertiveRef.current;
    if (element) {
      element.textContent = message;
      // Clear after announcement to allow repeated announcements
      setTimeout(() => {
        element.textContent = '';
      }, 1000);
    }
  }, []);

  const announceStatus = React.useCallback((message: string) => {
    announce(message, 'polite');
  }, [announce]);

  const announceAlert = React.useCallback((message: string) => {
    announce(message, 'assertive');
  }, [announce]);

  const value = React.useMemo(() => ({
    announce,
    announceStatus,
    announceAlert
  }), [announce, announceStatus, announceAlert]);

  return (
    <LiveRegionContext.Provider value={value}>
      {children}
      <div
        ref={politeRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
      <div
        ref={assertiveRef}
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      />
    </LiveRegionContext.Provider>
  );
}

// Create hook function separately for HMR stability
function createUseAnnouncement() {
  return () => {
    const context = React.useContext(LiveRegionContext);
    if (!context) {
      throw new Error('useAnnouncement must be used within a LiveRegionProvider');
    }
    return context;
  };
}

export const useAnnouncement = createUseAnnouncement();

// Component for manual live regions
interface ManualLiveRegionProps {
  children: React.ReactNode;
  priority?: 'polite' | 'assertive';
  atomic?: boolean;
  className?: string;
}

export function ManualLiveRegion({ 
  children, 
  priority = 'polite', 
  atomic = true, 
  className 
}: ManualLiveRegionProps) {
  return (
    <div
      aria-live={priority}
      aria-atomic={atomic}
      className={className}
    >
      {children}
    </div>
  );
}